import { CHAT_GW_URL, DEFAULT_HOME_SERVER } from 'src/logic/constants/chat.constants';
import { toast } from 'src/shared/utils/app-toast';

interface InitResponse {
  access_token: string;
  user_id: string;
  device_id?: string;
  home_server?: string;
}

async function fetchJson<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, init);

  if (res.ok) {
    if (res.status === 204) return undefined as unknown as T;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) return (await res.json()) as T;
    return (await res.text()) as unknown as T;
  }

  let detail = `Request failed (${res.status})`;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const errorResponse = await res.json().catch(() => null);
    detail = errorResponse?.detail ?? detail;
  } else {
    const text = await res.text().catch(() => '');
    detail = text || detail;
  }

  if (detail === 'Unauthorized') {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('device_id');
    localStorage.removeItem('home_server');
    localStorage.removeItem('app_id');
    window.location.reload();
  }

  toast.error(detail);
  throw new Error(detail);
}

class ChatAuthService {
  private readonly apiUrl = CHAT_GW_URL;
  private tokenKey = 'matrix_token';
  private userIdKey = 'matrix_user_id';

  async initSession(jwt: string, appId: string): Promise<InitResponse> {
    const url = `${this.apiUrl}/chat/init`;

    const data = await fetchJson<InitResponse>(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appId }),
    });

    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('user_id', data.user_id);
    localStorage.setItem('device_id', data.device_id ?? '');
    localStorage.setItem('home_server', data.home_server ?? DEFAULT_HOME_SERVER);
    localStorage.setItem('app_id', appId ?? '');

    return data;
  }

  async sendServiceMsg(chatId: string, msg: string): Promise<void> {
    const url = `${this.apiUrl}/chat/dialogs/service-message/${encodeURIComponent(chatId)}`;

    await fetchJson<void>(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: `___system-msg___:${msg}`,
      }),
    });
  }
}

export const chatAuthService = new ChatAuthService();
